/**
 * InventoryListScreen — desktop-native dense table.
 * - Single search bar (name / SKU / barcode), debounced
 * - Category filter chips (left rail style, inline)
 * - Click a row → opens edit drawer (ProductDetailPanel)
 * - "Add product" → ProductFormDialog (modal)
 * - Toolbar action: Adjust Stock for currently selected row
 *
 * All queries are reactive via WatermelonDB observe().
 */

import { productDisplayPrice } from '@/inventory/productDisplayPrice';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, ArrowLeftRight, Upload, LayoutGrid, List, Grid2x2, Grid3x3, Sparkles } from 'lucide-react';
import { database } from '@/db';
import { Product, Category } from '@/db/models/Inventory';
import { useAppStore } from '@/store/useAppStore';
import { useObservable } from '@/hooks/useObservable';
import { useObservableStatus } from '@/hooks/useObservableStatus';
import { useInfiniteWindow } from '@/hooks/useInfiniteWindow';
import { SkeletonInventoryTable, SkeletonInventoryGrid } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Dialog, DialogContent } from '@/components/ui/Dialog';
import { cn } from '@/utils/cn';
import { formatCurrency } from '@/utils/format';
import { Money, Qty, Code } from '@/components/ui/numeric';
import { ProductForm } from './ProductForm';
import { ProductDetailPanel } from './ProductDetailPanel';
import { StockAdjustForm } from './StockAdjustForm';
import { StockTransferDialog } from './StockTransferDialog';
import { RBACGate, INVENTORY_CREATE, INVENTORY_TRANSFER, INVENTORY_IMPORT, INVENTORY_ADJUST_STOCK } from '@/rbac';
import { PlanGate, UpgradeChip, PLAN_TIERS, FEATURE_STOCK_TRANSFER } from '@/plans';
import { MANUAL_ITEM_SKU } from '@/billing/manualItem';

type SortKey = 'name' | 'price' | 'stock' | 'updated';
type SortDir = 'asc' | 'desc';
type StockFilter = 'all' | 'low' | 'oos';

function isTracked(p: Product) {
  return p.trackInventory && !p.isService;
}
function isOutOfStock(p: Product) {
  return isTracked(p) && p.currentStock <= 0;
}
function isLowStock(p: Product) {
  return isTracked(p) && p.currentStock > 0 && p.currentStock <= p.lowStockAlert;
}

// ── View + grid density (device-local preferences) ────────────────────
type InvView = 'list' | 'grid';
type GridDensity = 'normal' | 'compact';

/** Grid column/padding per density (viewport breakpoints — the inventory
 *  pane is roughly full-width). */
const GRID_DENSITY: Record<GridDensity, { cols: string; pad: string }> = {
  normal: {
    cols: 'grid-cols-1 sm:grid-cols-2',
    pad: 'p-3.5',
  },
  compact: {
    cols: 'grid-cols-2 md:grid-cols-3 xl:grid-cols-4',
    pad: 'p-3',
  },
};

const INV_VIEW_KEY = 'sb:inventoryView';
const INV_DENSITY_KEY = 'sb:inventoryGridDensity';
// Grid is the default view, Compact the default density.
function loadView(): InvView {
  try { return localStorage.getItem(INV_VIEW_KEY) === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
}
function loadGridDensity(): GridDensity {
  try { return localStorage.getItem(INV_DENSITY_KEY) === 'normal' ? 'normal' : 'compact'; } catch { return 'compact'; }
}

export function InventoryListScreen() {
  const navigate = useNavigate();
  const activeStoreId = useAppStore((s) => s.activeStoreId);
  const merchantId = useAppStore((s) => s.currentUser?.merchantId);

  // ── Search (debounced) ────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), 180);
    return () => clearTimeout(t);
  }, [searchInput]);

  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState<string[]>([]);
  const [stockFilter, setStockFilter] = useState<StockFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const categoryMenuRef = useRef<HTMLDivElement | null>(null);

  const [openAdd, setOpenAdd] = useState(false);
  const [openTransfer, setOpenTransfer] = useState(false);
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [adjustingProduct, setAdjustingProduct] = useState<Product | null>(null);

  // View (list/grid) + grid density, persisted per-device.
  const [viewMode, setViewMode] = useState<InvView>(loadView);
  const [gridDensity, setGridDensity] = useState<GridDensity>(loadGridDensity);
  const changeView = (v: InvView) => {
    setViewMode(v);
    try { localStorage.setItem(INV_VIEW_KEY, v); } catch { /* ignore */ }
  };
  const changeDensity = (d: GridDensity) => {
    setGridDensity(d);
    try { localStorage.setItem(INV_DENSITY_KEY, d); } catch { /* ignore */ }
  };

  // ── Categories ────────────────────────────────────────────
  const categories = useObservable<Category[]>(
    () => database.get<Category>('categories').query(
      Q.where('is_deleted', false),
      ...(merchantId ? [Q.where('merchant_id', merchantId)] : []),
    ).observe(),
    [merchantId],
    [],
  );

  // Duplicate category names (from sync/import) are merged into one chip
  // that filters across every matching category id.
  const categoryGroups = useMemo(() => {
    const map = new Map<string, { key: string; label: string; ids: string[] }>();
    for (const c of categories) {
      const key = c.name.trim().toLowerCase();
      if (!key) continue;
      const existing = map.get(key);
      if (existing) existing.ids.push(c.id);
      else map.set(key, { key, label: c.name.trim(), ids: [c.id] });
    }
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [categories]);

  const selectedCategoryIds = useMemo(() => {
    if (selectedCategoryKeys.length === 0) return null;
    const ids = new Set<string>();
    for (const group of categoryGroups) {
      if (selectedCategoryKeys.includes(group.key)) {
        for (const id of group.ids) ids.add(id);
      }
    }
    return [...ids];
  }, [selectedCategoryKeys, categoryGroups]);

  function toggleCategoryKey(key: string) {
    setSelectedCategoryKeys((curr) => (curr.includes(key) ? curr.filter((k) => k !== key) : [...curr, key]));
  }

  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      if (!categoryMenuRef.current) return;
      if (!categoryMenuRef.current.contains(event.target as Node)) {
        setCategoryMenuOpen(false);
      }
    }
    function onEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setCategoryMenuOpen(false);
    }
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onEscape);
    };
  }, []);

  // ── Products (reactive, with loading signal) ──────────────
  const { data: products, loading: productsLoading } = useObservableStatus<Product[]>(
    () => {
      if (!activeStoreId) return null;
      const conditions: any[] = [
        Q.where('store_id', activeStoreId),
        Q.where('is_deleted', false),
        // The carrier row for manual bill lines is bookkeeping, not stock.
        Q.where('sku', Q.notEq(MANUAL_ITEM_SKU)),
      ];
      if (selectedCategoryIds && selectedCategoryIds.length > 0) {
        conditions.push(Q.where('category_id', Q.oneOf(selectedCategoryIds)));
      }
      if (searchQuery.length > 0) {
        const q = `%${searchQuery}%`;
        conditions.push(
          Q.or(
            Q.where('name', Q.like(q)),
            Q.where('barcode', Q.like(q)),
            Q.where('sku', Q.like(q)),
          ),
        );
      }
      return database.get<Product>('products').query(...conditions).observeWithColumns(['current_stock', 'selling_price', 'name']);
    },
    [activeStoreId, searchQuery, selectedCategoryIds?.join(',')],
    [],
  );

  // ── Stock status counts (before stock filter is applied) ─
  const lowCount = useMemo(() => products.filter(isLowStock).length, [products]);
  const oosCount = useMemo(() => products.filter(isOutOfStock).length, [products]);

  // ── Filter + sort in memory ───────────────────────────────
  const sortedProducts = useMemo(() => {
    let arr = [...products];
    if (stockFilter === 'low') arr = arr.filter(isLowStock);
    else if (stockFilter === 'oos') arr = arr.filter(isOutOfStock);
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (sortKey) {
        // Sort on the number the column actually shows. Products carry
        // different GST rates, so ordering by the stored ex-tax price can put
        // a visibly cheaper row above a dearer one once tax is added back.
        case 'price':   return (productDisplayPrice(a) - productDisplayPrice(b)) * dir;
        case 'stock':   return (a.currentStock - b.currentStock) * dir;
        case 'updated': return (a.updatedAt.getTime() - b.updatedAt.getTime()) * dir;
        default:        return a.name.localeCompare(b.name) * dir;
      }
    });
    return arr;
  }, [products, sortKey, sortDir, stockFilter]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  }

  // ── Windowed rendering — only a growing slice hits the DOM ─
  const { visible: visibleProducts, hasMore, sentinelRef } = useInfiniteWindow(sortedProducts, {
    pageSize: 60,
    resetKey: `${searchQuery}|${selectedCategoryIds?.join(',') ?? ''}|${stockFilter}|${sortKey}|${sortDir}`,
  });

  // Show the skeleton only on the very first load (no data yet), not on
  // every reactive re-query of an already-populated list.
  const showSkeleton = productsLoading && products.length === 0;

  if (!activeStoreId) {
    return <div className="text-fg-secondary text-body">No active store. Sign in again.</div>;
  }

  return (
    <div className="flex h-full flex-col gap-5">
      {/* Header
       *
       * Wraps. At 1024–1366 — which is most laptops — five buttons plus a
       * title do not fit on one line, and without `flex-wrap` the action group
       * simply overlapped "Inventory / 8 products". `items-start` so the title
       * stays put when the buttons drop to a second row. */}
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-display text-fg-primary">Inventory</h1>
          <p className="mt-1 text-body text-fg-secondary">
            {sortedProducts.length} {sortedProducts.length === 1 ? 'product' : 'products'}
            {selectedCategoryKeys.length > 0 ? ` in ${selectedCategoryKeys.length} categor${selectedCategoryKeys.length === 1 ? 'y' : 'ies'}` : ''}
            {stockFilter === 'low' ? ' · low stock' : stockFilter === 'oos' ? ' · out of stock' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <RBACGate requires={INVENTORY_IMPORT}>
            <Button variant="secondary" onClick={() => navigate('/inventory/import')}>
              <Upload size={14} /> Import CSV
            </Button>
          </RBACGate>
          <RBACGate requires={INVENTORY_IMPORT}>
            <Button variant="secondary" onClick={() => navigate('/inventory/ai-import')}>
              <Sparkles size={14} /> AI import
            </Button>
          </RBACGate>
          <RBACGate requires={INVENTORY_TRANSFER}>
            {/* Locked state renders as a chip, not the default banner — a
                toolbar is no place for a bordered explanation card. */}
            <PlanGate
              feature={FEATURE_STOCK_TRANSFER}
              fallback={<UpgradeChip label="Transfer" requiredTier={PLAN_TIERS.PRO} />}
            >
              <Button variant="secondary" onClick={() => setOpenTransfer(true)}>
                <ArrowLeftRight size={14} /> Transfer
              </Button>
            </PlanGate>
          </RBACGate>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Refresh
          </Button>
          <RBACGate requires={INVENTORY_CREATE}>
            <Button onClick={() => setOpenAdd(true)}>
              <Plus size={16} /> Add product
            </Button>
          </RBACGate>
        </div>
      </div>

      {/* Toolbar
       *
       * Also wraps, and the search keeps a floor width. It was `flex-1` with
       * no minimum, so once the filters and view toggles had taken their space
       * the field collapsed to roughly its own icon. */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[15rem] flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-tertiary" />
          <Input
            placeholder="Search by name, SKU, or barcode"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
          />
        </div>
        <div ref={categoryMenuRef} className="relative min-w-[240px]">
          <button
            type="button"
            onClick={() => setCategoryMenuOpen((v) => !v)}
            className="flex h-9 w-full items-center justify-between rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 text-body text-fg-primary"
          >
            <span className="truncate">
              {selectedCategoryKeys.length === 0
                ? 'All categories'
                : selectedCategoryKeys.length === 1
                  ? (categoryGroups.find((g) => g.key === selectedCategoryKeys[0])?.label ?? '1 category')
                  : `${selectedCategoryKeys.length} categories selected`}
            </span>
            <span className="ml-3 text-label text-fg-tertiary">▾</span>
          </button>
          {categoryMenuOpen && (
            <div className="absolute left-0 top-10 z-20 max-h-96 w-[420px] max-w-[75vw] overflow-auto rounded-md border border-stroke-hairline/20 bg-bg-surface p-2 shadow-e2">
              <button
                type="button"
                onClick={() => setSelectedCategoryKeys([])}
                className="mb-1 w-full rounded px-2 py-1.5 text-left text-body text-fg-secondary hover:bg-bg-subtle hover:text-fg-primary"
              >
                Clear all
              </button>
              <div className="grid grid-cols-1 gap-0.5 sm:grid-cols-2">
                {categoryGroups.map((g) => {
                  const checked = selectedCategoryKeys.includes(g.key);
                  return (
                    <label key={g.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-bg-subtle">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleCategoryKey(g.key)}
                        className="h-4 w-4 rounded border-stroke-hairline/40"
                      />
                      <span className="truncate text-body text-fg-primary">{g.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <select
          aria-label="Sort products"
          value={`${sortKey}:${sortDir}`}
          onChange={(e) => {
            const [k, d] = e.target.value.split(':') as [SortKey, SortDir];
            setSortKey(k);
            setSortDir(d);
          }}
          className="h-9 rounded-md border border-stroke-hairline/20 bg-bg-surface px-3 text-body text-fg-primary"
        >
          <option value="name:asc">Name (A–Z)</option>
          <option value="name:desc">Name (Z–A)</option>
          <option value="price:asc">Price (low → high)</option>
          <option value="price:desc">Price (high → low)</option>
          <option value="stock:asc">Stock (low → high)</option>
          <option value="stock:desc">Stock (high → low)</option>
          <option value="updated:desc">Recently updated</option>
        </select>
        <div className="ml-auto flex items-center gap-2">
          <StockFilterChip
            label="Low stock"
            count={lowCount}
            tone="warning"
            active={stockFilter === 'low'}
            onClick={() => setStockFilter((f) => (f === 'low' ? 'all' : 'low'))}
          />
          <StockFilterChip
            label="Out of stock"
            count={oosCount}
            tone="danger"
            active={stockFilter === 'oos'}
            onClick={() => setStockFilter((f) => (f === 'oos' ? 'all' : 'oos'))}
          />

          {/* Grid density — only meaningful in grid view */}
          {viewMode === 'grid' && (
            <div className="flex items-center rounded-md border border-stroke-hairline/20 p-0.5">
              <IconToggle active={gridDensity === 'normal'} onClick={() => changeDensity('normal')} title="Normal cards" label="Normal density">
                <Grid2x2 size={16} />
              </IconToggle>
              <IconToggle active={gridDensity === 'compact'} onClick={() => changeDensity('compact')} title="Compact cards" label="Compact density">
                <Grid3x3 size={16} />
              </IconToggle>
            </div>
          )}

          {/* List / Grid view toggle */}
          <div className="flex items-center rounded-md border border-stroke-hairline/20 p-0.5">
            <IconToggle active={viewMode === 'list'} onClick={() => changeView('list')} title="List view" label="List view">
              <List size={16} />
            </IconToggle>
            <IconToggle active={viewMode === 'grid'} onClick={() => changeView('grid')} title="Grid view" label="Grid view">
              <LayoutGrid size={16} />
            </IconToggle>
          </div>
        </div>
      </div>

      {/* Products — skeleton on first load, else windowed list / card grid */}
      {showSkeleton ? (
        viewMode === 'list'
          ? <SkeletonInventoryTable />
          : <SkeletonInventoryGrid cols={GRID_DENSITY[gridDensity].cols} />
      ) : viewMode === 'list' ? (
      <div className="flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface">
        <table className="w-full text-body">
          <thead className="sticky top-0 z-10 bg-bg-surface">
            <tr className="border-b border-stroke-hairline/10 text-label uppercase text-fg-tertiary">
              <Th sortable active={sortKey === 'name'} dir={sortDir} onClick={() => toggleSort('name')}>Name</Th>
              <Th>SKU / Barcode</Th>
              <Th align="right" sortable active={sortKey === 'price'} dir={sortDir} onClick={() => toggleSort('price')}>Price</Th>
              <Th align="right" sortable active={sortKey === 'stock'} dir={sortDir} onClick={() => toggleSort('stock')}>Stock</Th>
              <Th>Unit</Th>
              <Th align="right">GST</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {sortedProducts.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-16 text-center text-body text-fg-tertiary">
                  {searchQuery || selectedCategoryKeys.length > 0 || stockFilter !== 'all'
                    ? 'No matches. Try clearing filters.'
                    : 'No products yet. Click "Add product" to get started.'}
                </td>
              </tr>
            ) : (
              <>
                {visibleProducts.map((p) => (
                  <ProductRow
                    key={p.id}
                    product={p}
                    onClick={() => setEditingProductId(p.id)}
                    onAdjust={(e) => { e.stopPropagation(); setAdjustingProduct(p); }}
                  />
                ))}
                {hasMore && (
                  <tr ref={sentinelRef}>
                    <td colSpan={7} className="py-6 text-center text-label uppercase text-fg-tertiary">
                      Showing {visibleProducts.length} of {sortedProducts.length}…
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>
      ) : (
        <ProductGrid
          products={visibleProducts}
          total={sortedProducts.length}
          density={GRID_DENSITY[gridDensity]}
          onOpen={(p) => setEditingProductId(p.id)}
          onAdjust={(p) => setAdjustingProduct(p)}
          sentinelRef={hasMore ? sentinelRef : undefined}
          emptyHint={
            searchQuery || selectedCategoryKeys.length > 0 || stockFilter !== 'all'
              ? 'No matches. Try clearing filters.'
              : 'No products yet. Click "Add product" to get started.'
          }
        />
      )}

      {/* ── Add product dialog ── */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent title="Add product">
          {merchantId ? (
            <ProductForm
              storeId={activeStoreId}
              merchantId={merchantId}
              categories={categories}
              onSaved={() => setOpenAdd(false)}
              onCancel={() => setOpenAdd(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ── Product details (centered modal) ── */}
      <Dialog open={!!editingProductId} onOpenChange={(v) => !v && setEditingProductId(null)}>
        <DialogContent title="Product details" className="w-[820px] max-w-[96vw]">
          {editingProductId && merchantId && (
            <ProductDetailPanel
              productId={editingProductId}
              merchantId={merchantId}
              categories={categories}
              onClose={() => setEditingProductId(null)}
              onAdjust={(p) => { setEditingProductId(null); setAdjustingProduct(p); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Stock adjust dialog ── */}
      <Dialog open={!!adjustingProduct} onOpenChange={(v) => !v && setAdjustingProduct(null)}>
        <DialogContent title="Adjust stock" className="w-[620px] max-w-[95vw]">
          {adjustingProduct && (
            <StockAdjustForm
              product={adjustingProduct}
              onDone={() => setAdjustingProduct(null)}
              onCancel={() => setAdjustingProduct(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* ── Stock transfer dialog ── */}
      <Dialog open={openTransfer} onOpenChange={setOpenTransfer}>
        <DialogContent title="Transfer stock between godowns" className="sm:max-w-2xl">
          <StockTransferDialog
            onDone={() => setOpenTransfer(false)}
            onCancel={() => setOpenTransfer(false)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  ROW
// ─────────────────────────────────────────────────────────────────────────

function ProductRow({
  product,
  onClick,
  onAdjust,
}: {
  product: Product;
  onClick: () => void;
  onAdjust: (e: React.MouseEvent) => void;
}) {
  const isOOS = product.trackInventory && !product.isService && product.currentStock <= 0;
  const isLow = product.trackInventory && !product.isService && product.currentStock > 0 && product.currentStock <= product.lowStockAlert;

  return (
    <tr
      onClick={onClick}
      className="cursor-pointer border-b border-stroke-hairline/5 hover:bg-bg-subtle transition-colors duration-120 ease-apple"
    >
      <Td>
        <div className="font-medium text-fg-primary">{product.name}</div>
        {!product.isActive && (
          <div className="mt-0.5 text-label text-fg-tertiary">INACTIVE</div>
        )}
      </Td>
      <Td>
        <div className="text-fg-secondary">{product.sku || '—'}</div>
        {product.barcode && (
          <div className="text-label text-fg-tertiary">{product.barcode}</div>
        )}
      </Td>
      <Td align="right" className="font-medium tabular-nums">{formatCurrency(productDisplayPrice(product))}</Td>
      <Td align="right">
        {product.isService ? (
          <span className="text-fg-tertiary">—</span>
        ) : !product.trackInventory ? (
          <span className="text-fg-tertiary">∞</span>
        ) : (
          <span
            className={cn(
              'inline-flex items-center gap-1 tabular-nums',
              isOOS && 'text-danger-ink font-medium',
              isLow && !isOOS && 'text-warning-ink font-medium',
            )}
          >
            {product.currentStock}
            {isOOS && <span className="text-label uppercase">OOS</span>}
            {isLow && !isOOS && <span className="text-label uppercase">low</span>}
          </span>
        )}
      </Td>
      <Td className="text-fg-secondary">{product.unit}</Td>
      <Td align="right" className="tabular-nums text-fg-secondary">{product.gstRate}%</Td>
      <Td align="right">
        <RBACGate requires={INVENTORY_ADJUST_STOCK}>
          <button
            onClick={onAdjust}
            className="rounded-md px-2 py-1 text-label uppercase text-fg-secondary hover:bg-stroke-hairline/10 hover:text-fg-primary transition-colors"
            disabled={!product.trackInventory || product.isService}
          >
            Adjust
          </button>
        </RBACGate>
      </Td>
    </tr>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  TABLE PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────

function Th({
  children,
  align = 'left',
  sortable,
  active,
  dir,
  onClick,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
  sortable?: boolean;
  active?: boolean;
  dir?: SortDir;
  onClick?: () => void;
}) {
  return (
    <th
      onClick={onClick}
      className={cn(
        'whitespace-nowrap px-4 py-2.5 font-medium tracking-wide',
        align === 'right' ? 'text-right' : 'text-left',
        sortable && 'cursor-pointer select-none hover:text-fg-secondary',
      )}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortable && active && <span className="text-fg-secondary">{dir === 'asc' ? '↑' : '↓'}</span>}
      </span>
    </th>
  );
}

function Td({
  children,
  align = 'left',
  className,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <td
      className={cn(
        'px-4 py-3 align-middle',
        align === 'right' ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </td>
  );
}

function StockFilterChip({
  label,
  count,
  tone,
  active,
  onClick,
}: {
  label: string;
  count: number;
  tone: 'warning' | 'danger';
  active: boolean;
  onClick: () => void;
}) {
  const toneCls = tone === 'warning'
    ? (active ? 'border-warning/40 bg-warning/15 text-warning-ink' : 'border-stroke-hairline/15 text-fg-secondary hover:text-warning-ink hover:border-warning/30')
    : (active ? 'border-danger/40 bg-danger/15 text-danger-ink' : 'border-stroke-hairline/15 text-fg-secondary hover:text-danger-ink hover:border-danger/30');
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1 text-body transition-colors duration-120 ease-apple',
        toneCls,
      )}
    >
      <span className={cn('inline-block h-1.5 w-1.5 rounded-full', tone === 'warning' ? 'bg-warning' : 'bg-danger')} />
      {label}
      <span className="tabular-nums font-medium">{count}</span>
    </button>
  );
}


// ─────────────────────────────────────────────────────────────────────────
//  GRID VIEW
// ─────────────────────────────────────────────────────────────────────────

type GridProfile = { cols: string; pad: string };

function ProductGrid({
  products,
  total,
  density,
  onOpen,
  onAdjust,
  emptyHint,
  sentinelRef,
}: {
  products: Product[];
  total: number;
  density: GridProfile;
  onOpen: (p: Product) => void;
  onAdjust: (p: Product) => void;
  emptyHint: string;
  sentinelRef?: (node: Element | null) => void;
}) {
  if (products.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center rounded-lg border border-stroke-hairline/10 bg-bg-surface py-16 text-body text-fg-tertiary">
        {emptyHint}
      </div>
    );
  }
  return (
    <div className="flex-1 overflow-auto rounded-lg border border-stroke-hairline/10 bg-bg-surface p-3">
      <div className={cn('grid gap-3', density.cols)}>
        {products.map((p) => (
          <InventoryCard
            key={p.id}
            product={p}
            density={density}
            onOpen={() => onOpen(p)}
            onAdjust={() => onAdjust(p)}
          />
        ))}
      </div>
      {sentinelRef && (
        <div ref={sentinelRef} className="py-6 text-center text-label uppercase text-fg-tertiary">
          Showing {products.length} of {total}…
        </div>
      )}
    </div>
  );
}

function InventoryCard({
  product,
  density,
  onOpen,
  onAdjust,
}: {
  product: Product;
  density: GridProfile;
  onOpen: () => void;
  onAdjust: () => void;
}) {
  const tracked = isTracked(product);
  const oos = isOutOfStock(product);
  const low = isLowStock(product);
  return (
    <div
      className={cn(
        'flex flex-col justify-between overflow-hidden rounded-md border border-stroke-hairline/10 bg-bg-canvas transition-all duration-120 ease-apple hover:border-accent/40 hover:shadow-e1',
        !product.isActive && 'opacity-60',
        density.pad,
      )}
    >
      {/* Tap the card to open full product details (edit / delete / adjust).
       *
       * Stock leads, price follows. This screen exists to answer "what do I
       * have and what must I reorder" — the earlier version set the price in
       * bold charcoal and the stock in a muted grey pill, which answers the
       * question you came to Billing for, not this one.
       *
       * The coloured initial tile that used to sit here is gone. It carried no
       * information — first letter of the name, hue hashed from the category —
       * while being the most saturated thing on the card, and it cost the name
       * a third of its width on a screen where product names are long and
       * nearly identical ("Surf Excel Matic 2kg" / "Surf Excel Matic 4kg").
       */}
      <button type="button" onClick={onOpen} className="flex w-full flex-col items-start gap-2.5 text-left">
        <div className="min-w-0 w-full">
          <div className="line-clamp-2 text-body font-medium text-fg-primary">{product.name}</div>
          <Code className="mt-0.5 block truncate text-label text-fg-tertiary">
            {product.sku || product.barcode || '—'}
          </Code>
        </div>

        <div className="flex w-full items-baseline justify-between gap-2">
          {product.isService ? (
            <span className="text-body text-fg-tertiary">Service</span>
          ) : !product.trackInventory ? (
            <span className="text-body text-fg-tertiary">∞ {product.unit}</span>
          ) : (
            <Qty
              value={product.currentStock}
              unit={product.unit}
              className={cn(
                'text-total-sm font-semibold',
                // Colour only when it needs a decision. A healthy count stays
                // neutral, so yellow and red keep meaning something.
                oos ? 'text-danger-ink' : low ? 'text-warning-ink' : 'text-fg-primary',
              )}
            />
          )}
          <Money value={productDisplayPrice(product)} className="shrink-0 text-body text-fg-secondary" />
        </div>
      </button>

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-label text-fg-tertiary">
          {oos ? (
            <span className="text-danger-ink">Out of stock</span>
          ) : low ? (
            <span className="text-warning-ink">Reorder soon</span>
          ) : (
            `${product.gstRate}% GST`
          )}
        </span>
        <RBACGate requires={INVENTORY_ADJUST_STOCK}>
          <button
            type="button"
            onClick={onAdjust}
            disabled={!tracked}
            className="shrink-0 rounded px-1.5 py-0.5 text-label text-fg-secondary transition-colors hover:bg-stroke-hairline/10 hover:text-fg-primary disabled:opacity-40"
          >
            Adjust
          </button>
        </RBACGate>
      </div>
    </div>
  );
}

function IconToggle({
  active,
  onClick,
  title,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={label}
      className={cn(
        'rounded p-1.5 transition-colors',
        active ? 'bg-accent/10 text-accent-ink' : 'text-fg-tertiary hover:text-fg-primary',
      )}
    >
      {children}
    </button>
  );
}
